import { ptBR } from "date-fns/locale";
import { ChartData } from "libLegacy/api/models";

/**
 * Called by Victory, generates a "mmm/YY" string to be displayed as an X axis labed based on the numeric
 * representation of a Date that Victory requires to plot X points in the chart.
 * @param xValue current Y value being plotted by Victory, the return from "normalizeTimeValues"
 * @returns a mmm/YY string from the original date
 */
export const formatTimeLabel = (xValue: number) => {
  if (!xValue) {
    return "";
  }
  const refDate = new Date(xValue);
  const month = refDate.toLocaleDateString(ptBR.code, { month: "short" });
  const year = refDate.toLocaleDateString(ptBR.code, { year: "2-digit" });

  return `${month.replace(".", "")}/${year}`;
};

export const formatDayMonthLabel = (xValue: number) => {
  if (!xValue) {
    return "";
  }
  const refDate = new Date(xValue);
  const day = refDate.toLocaleDateString(ptBR.code, { day: "2-digit" });
  const month = refDate.toLocaleDateString(ptBR.code, { month: "2-digit" });

  return `${day}/${month}`;
};

export const formatHourLabel = (xValue: number) => {
  if (!xValue) {
    return "";
  }
  const refDate = new Date(xValue);
  const hour = refDate.toLocaleString(ptBR.code, {
    hour: "numeric",
  });

  return `${hour}h`;
};

/**
 * Called by Victory, formats the chart point's value field into a "R$ X.XXX,xx" string. The values are
 * shortened based on the magnitude of the points (e.g. 1100040.76 would be"RS 1.1 mi") for a better fit
 * @param yValue current Y value being plotted by Victory, not necessarily the original Y value
 * @returns "R$ X.XXX,xx"
 */
export const formatMoneyLabel = (yValue: ChartData["value"]) =>
  yValue.toLocaleString(ptBR.code, {
    style: "currency",
    currency: "BRL",
    notation: "compact",
    compactDisplay: "long",
  });

/**
 * Called by Victory, formats the chart point's value field into a "X.XXX,xx kWh" string. The values are
 * shortened based on the magnitude of the points (e.g. 1100040.76 would be"1.1 mi kWh") for a better fit
 * @param yValue current Y value being plotted by Victory, not necessarily the original Y value
 * @returns "X.XXX,xx kWh"
 */
export const formatKwhLabel = (yValue: ChartData["value"]) =>
  `${(+yValue).toLocaleString(ptBR.code, {
    notation: "compact",
    compactDisplay: "long",
  })} kWh`;

/**
 * Called by Victory, formats the chart point's value field into a "X.XXX,xx C02e" string. The values are
 * shortened based on the magnitude of the points (e.g. 1100040.76 would be"1.1 mi kWh") for a better fit
 * @param yValue current Y value being plotted by Victory, not necessarily the original Y value
 * @returns "X.XXX,xx kWh"
 */
export const formatCO2eLabel = (yValue: ChartData["value"]) =>
  `${(+yValue).toLocaleString(ptBR.code, {
    notation: "compact",
    compactDisplay: "long",
  })} CO2e`;

/**
 * Called by Victory, is used to format the ISO datetime string we get from the backend into a numeric representation
 * of that same time that Victory can understand and use. This is required because Victory has a very limited and somewhat
 * faulty support for strings/dates as point coordinates, so it's safer to turn it into a number so we can take advantage of
 * the extensive numeric point handling features Victory offers.
 * The return of this function is fed to any subsequent formatters Victory calls on the axis it was applied to, like formatTimeLabel
 * @param time date
 * @returns date in millisseconds since epoch
 */
export const normalizeTimeValues = ({ time }: ChartData) =>
  new Date(time).valueOf();

/**
 * Placeholder labels should be used if, in a VALID chart, every Y is zero
 * @param data
 * @returns
 */
export const usePlaceholderLabels = (
  data: Array<ChartData> | undefined
): boolean => !!data && data.every(({ value }) => value === 0);

export const formatMonthLabel = (xValue: number) => {
  if (!xValue) {
    return "";
  }
  const refDate = new Date(xValue);
  const month = refDate.toLocaleDateString(ptBR.code, { month: "short" });

  return `${month.replace(".", "")}`;
};
